import React, { useEffect, useState } from 'react';
import styles from './ShareSystemDialog.module.scss';
import { Box, Button, Grid, Modal, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ProjectService from '../../services/ProjectService';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SaveProjectSharesRequest, SystemShareInfo, SystemShareReason, SystemShareStatus } from '../../models/Api';
import { getUserFullName } from '../../utils/Utils';

export interface ShareProjectDialogProps {
  projectId: string;
  projectName: string;
  isModalOpen: boolean;
  closeAction: () => void;
}

const ShareProjectDialog = (props: ShareProjectDialogProps) => {
  const { projectId, projectName, isModalOpen, closeAction } = props;
  const { t } = useTranslation();
  const [emailToAdd, setEmailToAdd] = useState<string>('');
  const [emailToAddHelperText, setEmailToAddHelperText] = useState<string>('');
  const [emailToAddIsValid, setEmailToAddIsValid] = useState<boolean>(true);
  const [shares, setShares] = useState<SystemShareInfo[]>([]);
  const [originalShares, setOriginalShares] = useState<SystemShareInfo[]>([]);
  const [yourName, setYourName] = useState<string>(getUserFullName());
  const [message, setMessage] = useState<string>('');

  const emailToAddSchema = yup.string().trim().required().max(512).email();

  async function handleSave() {
    const request: SaveProjectSharesRequest = {
      projectId,
      newShares: shares.filter((share) => share.id === 0),
      updatedShares: [],
      deletedShareIds: originalShares.filter((share) => !shares.includes(share)).map((share) => share.id),
      userFullName: yourName || getUserFullName(),
      message,
    };
    if (request.newShares.length + request.deletedShareIds.length + request.updatedShares.length > 0) {
      const response = await ProjectService.saveProjectShares(projectId, request);
      if (
        response &&
        (response.deletedSharesCount !== request.deletedShareIds.length ||
          response.newSharesCount !== request.newShares.length ||
          response.updatedSharesCount !== request.updatedShares.length)
      ) {
        console.error('Mismatched count of results while saving shares for project', projectId, request, response);
      }
    }
    closeAction();
  }

  function resetEmailToAdd() {
    setEmailToAdd('');
    setEmailToAddHelperText('');
    setEmailToAddIsValid(true);
  }

  useEffect(() => {
    resetEmailToAdd();
    setYourName(getUserFullName());
    setMessage('');

    async function getProjectShares() {
      const projectShares = (await ProjectService.getProjectShares(projectId)) ?? [];
      const filteredShares = projectShares.filter((projectShare) => projectShare.shareReason === SystemShareReason.Collaboration);
      setOriginalShares(filteredShares);
      setShares(filteredShares);
    }
    if (isModalOpen) {
      getProjectShares();
    }
  }, [isModalOpen]);

  async function handleEmailToAddButtonClick(): Promise<void> {
    try {
      await emailToAddSchema.validate(emailToAdd);
    } catch (error) {
      setEmailToAddIsValid(false);
      const validationError = error as yup.ValidationError;
      setEmailToAddHelperText(validationError.errors[0]);
      return;
    }
    if (shares.some((share) => share.email.toLowerCase() === emailToAdd.toLowerCase())) {
      setEmailToAddHelperText(t('EmailAlreadyShared'));
      return;
    }

    const existingShare = originalShares.find((share) => share.email.toLowerCase() === emailToAdd.toLowerCase());
    const newShare: SystemShareInfo = {
      id: 0,
      projectId,
      email: emailToAdd,
      status: SystemShareStatus.Initiated,
      isEditable: true,
      shareReason: SystemShareReason.Collaboration,
    };
    const shareToAdd = existingShare ? existingShare : newShare;
    setShares([...shares, shareToAdd]);
    resetEmailToAdd();
  }

  function handleDeleteEmail(deletedEmail: string): void {
    setShares(shares.filter((share) => share.email.toLowerCase() !== deletedEmail.toLowerCase()));
  }

  return (
    <Modal
      open={isModalOpen}
      onClose={() => {
        closeAction();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className={styles['share-dialog']}>
        <div className={styles['title-div']}>
          <p>{t('ShareSettings')}</p>
        </div>
        <div className={styles['input-wrapper']}>
          <div>
            <TextField id="share-system-name" label={t('ProjectName')} value={projectName} disabled></TextField>
          </div>
          <div>
            <p>{t('ProjectSharingInfo')}</p>
            <p>{t('SharedWith')}</p>
            <Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={10}>
                  <TextField
                    id="share-with-email-address"
                    label={t('EmailAddress')}
                    helperText={emailToAddHelperText}
                    error={!emailToAddIsValid}
                    value={emailToAdd}
                    autoFocus={true}
                    onChange={(e) => setEmailToAdd(e.target.value)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    onClick={() => handleEmailToAddButtonClick()}
                    variant="outlined"
                    startIcon={<FontAwesomeIcon icon="user-plus" />}
                    color="secondary"
                    className={styles['add-email-button']}
                  >
                    {t('Add')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
            <table className={styles['share-with-list']}>
              <thead>
                <tr>
                  <th scope="col" className={styles['column-delete-header']}>
                    {t('Delete')}
                  </th>
                  <th scope="col" className={styles['column-header']}>
                    {t('EmailAddress')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {shares.length === 0 && (
                  <tr>
                    <td></td>
                    <td scope="row">
                      <em>{t('EmptySharedWithList')}</em>
                    </td>
                  </tr>
                )}
                {shares.map((share, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        <Button onClick={() => handleDeleteEmail(share.email)} color="error">
                          <FontAwesomeIcon icon="trash-can" />
                        </Button>
                      </td>
                      <td scope="row" style={{ color: share.id === 0 ? 'navy' : 'black' }}>
                        {share.email}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p>{t('ShareNotificationHeading')}</p>
          <div style={{ marginTop: 8 }}>
            <TextField
              id="your-name"
              placeholder={getUserFullName()}
              inputProps={{ maxLength: 256 }}
              label={t('YourName')}
              value={yourName}
              onChange={(e) => setYourName(e.target.value)}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <TextField
              maxRows={4}
              multiline
              id="shared-message"
              inputProps={{ maxLength: 1000 }}
              label={t('Message')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>

        <div className={styles['buttons-div']}>
          <Button onClick={() => handleSave()}>{t('Save')}</Button>
          <Button onClick={() => props.closeAction()}>{t('Cancel')}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default ShareProjectDialog;
